import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiFileText 
} from 'react-icons/fi';
import { API_BASE_URL } from '../config/config';

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.get(`${API_BASE_URL}/blogs`, {
        headers: { 'X-User-ID': userId }
      });
      
      if (response.data.success) {
        setBlogs(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Error loading blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.delete(`${API_BASE_URL}/blog/${id}`, {
        headers: { 'X-User-ID': userId }
      });

      if (response.data.success) {
        toast.success('Blog deleted successfully');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Error deleting blog');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/blog/${id}/toggle-status`,
        {},
        { headers: { 'X-User-ID': userId } }
      );

      if (response.data.success) {
        toast.success('Blog status updated');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Error updating blog status');
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Blog Articles</h1>
          <p className="text-gray-600">Manage your institute's blog content</p>
        </div>
        <Link
          to="/blogs/create"
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus /> Create New Blog
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : blogs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FiFileText size={64} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No blogs yet</h3>
          <p className="text-gray-600 mb-6">Start creating engaging content for your students</p>
          <Link
            to="/blogs/create"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus /> Create Your First Blog
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {blogs.map((blog) => (
            <div key={blog.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="flex">
                {blog.image && (
                  <div className="w-64 h-48 flex-shrink-0">
                    <img 
                      src={blog.image} 
                      alt={blog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="flex-1 p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{blog.title}</h3>
                      <p className="text-sm text-gray-500 mb-2">
                        URL: <span className="text-blue-600">{blog.url}</span>
                      </p>
                      {blog.short_description && (
                        <p className="text-gray-600 text-sm mb-3">
                          {truncateText(blog.short_description, 200)}
                        </p>
                      )}
                      {!blog.short_description && blog.description && (
                        <p className="text-gray-600 text-sm mb-3">
                          {truncateText(stripHtml(blog.description), 200)}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => toggleStatus(blog.id)}
                        className={`p-2 rounded ${
                          blog.is_active == 1 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                        title={blog.is_active == 1 ? 'Published' : 'Draft'}
                      >
                        {blog.is_active == 1 ? <FiEye size={20} /> : <FiEyeOff size={20} />}
                      </button>
                      
                      <Link
                        to={`/blogs/edit/${blog.id}`}
                        className="p-2 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                      >
                        <FiEdit2 size={20} />
                      </Link>
                      
                      <button
                        onClick={() => handleDelete(blog.id)}
                        className="p-2 rounded bg-red-100 text-red-600 hover:bg-red-200"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Created: {new Date(blog.cdate).toLocaleDateString()}</span>
                    {blog.udate && (
                      <span>Updated: {new Date(blog.udate).toLocaleDateString()}</span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      blog.is_active == 1 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {blog.is_active == 1 ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Blogs;

